/**
 * پیش‌فروش (Pre-sale)
 *
 * سرور تصمیم می‌گیرد؛ وب هیچ فلگی نمی‌فرستد. همه‌ی زمان‌ها «ساعت» می‌آیند و
 * تبدیل به «روز» با خود وب است.
 *
 * هسته (core) دست‌نخورده می‌ماند: `useProduct` موجودی را فقط از `details.count`
 * می‌خواند و واریانتِ پیش‌فروش (موجودیِ صفر) را «ناموجود» می‌بیند. به‌جای ویرایش
 * هسته، پیش از دادن داده به هسته، `details.count` را با `presale.remaining`
 * جایگزین می‌کنیم؛ این‌طور دکمه‌ی «افزودن به سبد»، سقف شمارنده و فعال بودنِ واریانت
 * خودکار درست می‌شوند.
 */

export const PRESALE_LABEL_KEY = "pre-sale";

/** فیلدهایی که روی واریانت (MixVariantValue) از سرور گرفته می‌شود. */
export const PRESALE_VARIANT_FIELDS = "presale { available remaining lead_hours }";

/** `{ ... }` را می‌گیرد و فیلد پیش‌فروش را به انتهای انتخابش اضافه می‌کند. */
export const append_presale_to_selection = (selection) =>
  selection.replace(/\}\s*$/, `  ${PRESALE_VARIANT_FIELDS}\n}`);

/**
 * فیلد پیش‌فروش را داخل هر بلوک `mix_variant { ... }` یک مدل یا کوئریِ کامل
 * (مثل `widget_product` هسته برای کارت محصول، یا کوئری صفحه با ویجت‌هایش)
 * می‌گذارد. بدون آن کارت‌ها واریانتِ پیش‌فروش (موجودیِ صفر) را «ناموجود» نشان
 * می‌دادند.
 */
export const append_presale_to_mix_variant = (selection) =>
  selection.replace(/mix_variant\s*\{/g, `mix_variant {\n          ${PRESALE_VARIANT_FIELDS}`);

/**
 * ساعت → روز. گرد کردن به بالا (۳۶ ساعت = ۲ روز) تا مشتری هیچ‌وقت زودتر از
 * زمان واقعی منتظر تحویل نماند. کمینه ۱ روز.
 */
export const presale_days = (hours) => {
  const value = Number(hours);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.max(1, Math.ceil(value / 24));
};

/** «۳ روز» با ارقام فارسی؛ برای ساعت نامعتبر رشته‌ی خالی. */
export const format_presale_days = (hours) => {
  const days = presale_days(hours);
  return days > 0 ? `${days.toLocaleString("fa-IR")} روز` : "";
};

/** `Date` سرور ممکن است عدد میلی‌ثانیه (به‌صورت رشته) یا رشته‌ی تاریخ باشد. */
export const presale_timestamp = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value;
  if (/^\d+$/.test(String(value))) return parseInt(value, 10);
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/** آیا این واریانت همین حالا به‌صورت پیش‌فروش قابل سفارش است؟ */
export const is_presale_available = (mix_variant) =>
  mix_variant?.presale?.available === true;

/**
 * نسخه‌ی یک واریانت که هسته آن را «موجود» می‌بیند.
 * - `details.count` ← تعداد باقی‌مانده‌ی پیش‌فروش
 * - برچسب پیش‌فروش فقط اگر برچسب دیگری (شگفت‌انگیز، سفارشی، ...) نباشد؛
 *   آن برچسب‌ها اولویت دارند.
 */
export const as_presale_variant = (mix_variant, remaining) => {
  if (!mix_variant) return mix_variant;
  const count = Number.isFinite(Number(remaining)) ? Number(remaining) : 0;
  return {
    ...mix_variant,
    details: { ...(mix_variant.details || {}), count },
    custom_fields: {
      ...(mix_variant.custom_fields || {}),
      custom_offer_label:
        mix_variant.custom_fields?.custom_offer_label || PRESALE_LABEL_KEY,
    },
  };
};

/** `data` محصول را آماده‌ی دادن به `useProduct` هسته می‌کند. */
export const with_presale_stock = (data) => {
  if (!data?.mix_variant?.some(is_presale_available)) return data;
  return {
    ...data,
    mix_variant: data.mix_variant.map((mix_variant) =>
      is_presale_available(mix_variant)
        ? as_presale_variant(mix_variant, mix_variant.presale.remaining)
        : mix_variant,
    ),
  };
};

/**
 * وضعیت‌هایی از سفارش که ژومنت همه را «در حال آماده‌سازی» نشان می‌دهد:
 * ۲ (complete)، ۳ (pending)، ۴ (packing). متنِ پیش‌فروش در هر سه می‌آید.
 */
export const ORDER_PREPARING_STATUSES = [2, 3, 4];

export const is_order_preparing = (order) =>
  ORDER_PREPARING_STATUSES.includes(order?.status);

/**
 * تاریخ آماده‌شدنِ کل سفارش (میلی‌ثانیه) یا ۰.
 * سرور آن را حساب می‌کند: تاریخ تأیید + زمان آماده‌سازیِ فروشنده + (برای ردیف‌های
 * پیش‌فروش) زمان تحویل پیش‌فروش؛ ردیف‌های ردشده نمی‌آیند. برای وضعیت ۰ و ۱ و سبد
 * `null` است. `presale_ready_at` ردیف فقط زمان تحویل پیش‌فروش را دارد و برای این
 * نمایش استفاده نمی‌شود.
 */
export const order_prepare_until = (order) =>
  is_order_preparing(order) ? presale_timestamp(order?.prepare_until) : 0;

/** تاریخ شمسی با صفر پیشرو، مثل ۱۴۰۵/۰۷/۳۰ (میلی‌ثانیه ورودی). */
export const format_presale_date = (timestamp) =>
  timestamp > 0
    ? new Intl.DateTimeFormat("fa-IR", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(timestamp))
    : "";

/** ردیف پیش‌فروشِ سفارشی که در حال آماده‌سازی است؟ */
export const is_order_row_presale = (order, row) =>
  is_order_preparing(order) && row?.is_presale === true;

/** بیشترین زمان آماده‌سازی (ساعت) بین ردیف‌های پیش‌فروشِ سفارشِ در حال آماده‌سازی، یا ۰. */
export const order_presale_lead_hours = (order) =>
  Math.max(
    0,
    ...(order?.products || [])
      .filter((row) => is_order_row_presale(order, row))
      .map((row) => Number(row?.presale_lead_hours) || 0),
  );
